import { readApiJson } from "./apiResponse";

export function getSequenceCampaignType(sequence = {}) {
  let settings = sequence.settings_json ?? sequence.settings ?? {};
  if (typeof settings === "string") {
    try { settings = JSON.parse(settings); } catch { settings = {}; }
  }
  return String(settings?.campaignType || "recruiting").trim().toLowerCase();
}

export function normalizeSequenceString(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

export function normalizeSequenceText(value) {
  return String(value || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
}

export function normalizeSequenceEntityType(value) {
  const normalized = normalizeSequenceString(value).toLowerCase();
  if (["company", "companies"].includes(normalized)) return "client";
  if (["candidate", "lead", "client", "job"].includes(normalized)) return normalized;
  return normalized || "candidate";
}

export function getSequenceEntityLabel(value, plural = false) {
  const entityType = normalizeSequenceEntityType(value);
  const labels = {
    candidate: plural ? "Candidates" : "Candidate",
    lead: plural ? "Leads" : "Lead",
    client: plural ? "Clients" : "Client",
    job: plural ? "Jobs" : "Job",
  };
  return labels[entityType] || (plural ? "Records" : "Record");
}

export function normalizeSequenceItem(item = {}, entityType = "candidate") {
  const normalizedType = normalizeSequenceEntityType(
    item.entityType || item.entity_type || entityType
  );
  const entityId = item.entityId ?? item.entity_id ?? item.id ?? null;
  return {
    entityType: normalizedType,
    entityId,
    name: normalizeSequenceString(item.name || item.full_name || item.fullName),
    title: normalizeSequenceString(item.title),
    company: normalizeSequenceString(item.company || item.company_name || item.companyName),
    email: normalizeSequenceString(item.email),
    phone: normalizeSequenceString(item.phone),
    linkedinUrl: normalizeSequenceString(item.linkedinUrl || item.linkedin_url),
    location: normalizeSequenceString(item.location),
    owner: normalizeSequenceString(item.owner || item.owner_name || item.ownerName),
    metadata: item.metadata && typeof item.metadata === "object" ? item.metadata : {},
  };
}

export const STRUCTURED_RECRUITING_SEQUENCE_BODY = [
  `{{company_description}}`,
  ``,
  `Who We Are`,
  `{{company_mission_vision}}`,
  ``,
  `Responsibilities:`,
  `{{job_responsibilities}}`,
  ``,
  `Qualifications:`,
  `{{job_qualifications}}`,
  ``,
  `Technical Expertise:`,
  `{{job_skills}}`,
  ``,
  `Company Snapshot:`,
  `Industry: {{company_industry}} | Founded: {{company_founded_year}} | Ownership: {{company_ownership}} | Company Size: {{company_size}}`,
  `Location: {{company_location}} | Website: {{company_website}} | Career Website: {{company_career_website}}`,
  ``,
  `Key Products / Services:`,
  `{{company_key_products}}`,
  ``,
  `Role Overview:`,
  `{{job_role_overview}}`,
  ``,
  `Role Details:`,
  `Employment: {{job_employment}} | Work Arrangement: {{job_work_arrangement}} | Salary: {{job_salary}} | Location: {{job_location}} | Industry: {{job_industry}}`,
].join("\n");

export const LEGACY_CANDIDATE_SEQUENCE_BODY = `Hi {{first_name}},\n\nI came across your profile and wanted to reach out. I think there could be a great fit based on your background, and I'd love to connect and share more details.\n\nWould you be open to a quick chat this week?\n\nBest,\n{{sender_name}}`;

export function createDefaultSequenceSteps(entityType = "candidate") {
  const singular = getSequenceEntityLabel(entityType);
  const normalized = String(entityType || "candidate").toLowerCase();

  const emailBody = (normalized === "job" || normalized === "client" || normalized === "lead")
    ? STRUCTURED_RECRUITING_SEQUENCE_BODY
    : LEGACY_CANDIDATE_SEQUENCE_BODY;

  return [
    {
      id: "step-1",
      channel: "email",
      title: "Intro email",
      subject: `${singular} introduction`,
      body: emailBody,
      delayValue: 0,
      delayUnit: "days",
      senderName: "",
      senderEmail: "",
    },
    {
      id: "step-2",
      channel: "linkedin",
      title: "LinkedIn follow-up",
      subject: "",
      body: `Hi {{first_name}},\n\nFollowing up here in case email is easier to miss.`,
      delayValue: 2,
      delayUnit: "days",
      senderName: "",
      senderEmail: "",
    },
    {
      id: "step-3",
      channel: "call",
      title: "Call / manual task",
      subject: "",
      body: `Review this ${singular.toLowerCase()} and follow up with a personal call.`,
      delayValue: 4,
      delayUnit: "days",
      senderName: "",
      senderEmail: "",
    },
  ];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    ""
  );
}

async function requestJson({ url, method = "GET", body }) {
  const token = getToken();
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(token ? { "x-access-token": token } : {}),
    ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
  };

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  return readApiJson(response, "Sequences");
}

function getApiBase() {
  return String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
}

export async function fetchSequences({ search = "", entityType = "" } = {}) {
  const apiBase = getApiBase();
  const params = new URLSearchParams();
  if (normalizeSequenceString(search)) params.set("search", normalizeSequenceString(search));
  if (normalizeSequenceString(entityType)) {
    params.set("entity_type", normalizeSequenceEntityType(entityType));
  }

  const query = params.toString();
  const payload = await requestJson({
    url: `${apiBase}/api/sequences${query ? `?${query}` : ""}`,
    method: "GET",
  });

  if (!Array.isArray(payload?.rows)) throw new Error("Invalid sequences response. Please try again.");
  return payload.rows;
}

export async function createSequence(payload) {
  const apiBase = getApiBase();
  const result = await requestJson({
    url: `${apiBase}/api/sequences`,
    method: "POST",
    body: payload,
  });
  window.dispatchEvent(new Event("rpo:activities-refresh"));
  return result;
}

export async function updateSequence(id, payload) {
  const apiBase = getApiBase();
  const result = await requestJson({
    url: `${apiBase}/api/sequences/${encodeURIComponent(id)}`,
    method: "PUT",
    body: payload,
  });
  window.dispatchEvent(new Event("rpo:activities-refresh"));
  return result;
}

export async function fetchSequenceById(id) {
  const apiBase = getApiBase();
  return requestJson({
    url: `${apiBase}/api/sequences/${encodeURIComponent(id)}`,
    method: "GET",
  });
}

export async function addSequenceEnrollments(sequenceId, enrollments) {
  const apiBase = getApiBase();
  const result = await requestJson({
    url: `${apiBase}/api/sequences/${encodeURIComponent(sequenceId)}/enrollments`,
    method: "POST",
    body: { enrollments },
  });
  window.dispatchEvent(new Event("rpo:activities-refresh"));
  return result;
}

export async function sendSequenceTestEmail(payload) {
  const apiBase = getApiBase();
  return requestJson({
    url: `${apiBase}/api/sequences/test-email`,
    method: "POST",
    body: payload,
  });
}
